import { Lightning, Router } from '@lightningjs/sdk';
import Button from 'components/common/buttons/Button';
import { getFontFaceFromStyle } from 'support/textUtils';
import { reportGetStarted } from 'services/analytics/reportingServicePage';
import { PageId } from 'types/pageId';
import { RoutePath } from 'config/routes';
import TextLabel from 'components/common/TextLabel';

export interface GetStartedSectionTemplateSpec
  extends Lightning.Component.TemplateSpec {
  icon: Lightning.Element.PatchTemplate;
  title: string;
  description: string;
  buttonLabel: string;

  Icon: typeof Lightning.Texture;
  Title: typeof TextLabel;
  Description: typeof TextLabel;
  Button: typeof Button;
}

const CONTENT_WIDTH = 1685;
const CONTENT_HEIGHT = 375;
const MARGIN_SPACE = 10;
const FONT_SIZE_TITLE = 34;
const FONT_SIZE_DESCRIPTION = 24;
const FONT_SIZE_BUTTON = 23;
const BUTTON_HEIGHT = 60;
const BUTTON_MIN_WIDTH = 170;

export default class GetStartedSection
  extends Lightning.Component<GetStartedSectionTemplateSpec>
  implements
    Lightning.Component.ImplementTemplateSpec<GetStartedSectionTemplateSpec>
{
  private _icon: GetStartedSectionTemplateSpec['icon'] = {};
  private _title = '';
  private _description = '';
  private _buttonLabel = '';

  private _Icon = this.getByRef('Icon')!;
  private _Title = this.getByRef('Title')!;
  private _Description = this.getByRef('Description')!;
  private _Button = this.getByRef('Button')!;

  get announce() {
    return [this._Title.text?.text, this._Description.text?.text];
  }

  get button() {
    return this._Button;
  }

  set icon(icon: GetStartedSectionTemplateSpec['Icon']) {
    this._icon = icon;
  }

  set title(title: string) {
    this._title = title;
  }

  set description(description: string) {
    this._description = description;
  }

  set buttonLabel(buttonLabel: string) {
    this._buttonLabel = buttonLabel;
  }

  static override _template(): Lightning.Component.Template<GetStartedSectionTemplateSpec> {
    return {
      w: CONTENT_WIDTH,
      h: CONTENT_HEIGHT,
      flex: {
        direction: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      },
      Icon: {
        flexItem: { marginBottom: MARGIN_SPACE },
      },
      Title: {
        type: TextLabel,
        flexItem: { marginBottom: MARGIN_SPACE },
        text: {
          fontSize: FONT_SIZE_TITLE,
          fontFace: getFontFaceFromStyle('bold'),
        },
      },
      Description: {
        type: TextLabel,
        flexItem: { marginBottom: MARGIN_SPACE },
        text: {
          fontSize: FONT_SIZE_DESCRIPTION,
          fontFace: getFontFaceFromStyle('regular'),
          textAlign: 'center',
        },
      },
      Button: {
        type: Button,
        height: BUTTON_HEIGHT,
        minWidth: BUTTON_MIN_WIDTH,
        fontSize: FONT_SIZE_BUTTON,
        fontFace: getFontFaceFromStyle('regular'),
        action: '$getStarted',
        signals: {
          $getStarted: true,
        },
        passSignals: { $onHover: true },
      },
    };
  }

  override _active() {
    this._Icon.patch(this._icon);
    this._Title.patch({ text: { text: this._title } });
    this._Description.patch({ text: { text: this._description } });
    this._Button.patch({ label: this._buttonLabel });
  }

  override _getFocused() {
    return this._Button;
  }

  private $getStarted() {
    // Report CTV_get_started event
    reportGetStarted(PageId.MY_STUFF);

    Router.navigate(RoutePath.LOGIN);
  }
}
